import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  Input,
  Textarea,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Skeleton,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { useToast } from '@vnetwork/ui/feedback';
import {
  useWaapVulnRule,
  useUpdateWaapVulnRule,
  useWaapDataLossApiAppOptions,
  useWaapVulnAssetOptions,
  type WaapVulnRule,
} from '@/api/waap';
import { WaapConsoleBreadcrumbs } from '@/pages/services/waap/WaapConsoleBreadcrumbs';
import { useWaapWebsite } from '@/pages/services/waap/waap-context';
import { errMessage } from '@/pages/services/waap/waap-util';
import { trackFeatureAction } from '@/lib/analytics-events';
import styles from './ApiProtectionVulnerabilityPage.module.css';

// Whitelist Metrics: app (Application) → /api_app/options, api (API) →
// /api_asset/options_v2 (confirmed from the vendor capture: key "app", value is
// the option id e.g. ["57"], NOT the label). Matching operation is a string —
// "equal" confirmed; "not_equal" follows the vendor's not_contain underscore
// style (verify against a captured Not-equal entry if one exists).
const WL_METRICS = [
  { value: 'app', label: 'Application' },
  { value: 'api', label: 'API' },
];
const WL_MATCHES = [
  { value: 'equal', label: 'Equal to' },
  { value: 'not_equal', label: 'Not equal to' },
];

const TABS = [
  { slug: 'detail', label: 'Detail' },
  { slug: 'whitelist', label: 'Whitelist' },
] as const;

const clone = (r: WaapVulnRule): WaapVulnRule => ({
  ...r,
  config: { ...r.config, whitelist: (r.config.whitelist ?? []).map((w) => ({ ...w, value: [...(w.value ?? [])] })) },
});

/** Managed vulnerability rule editor. Detail (read-only) + Whitelist. */
export function ApiProtectionVulnerabilityRulePage() {
  const { serviceId, ruleId } = useParams<{ serviceId: string; ruleId: string }>();
  // Account-level: a representative domain resolves the customer (no select-website gate).
  const { selected } = useWaapWebsite();
  const domainId = selected?.domainId ?? '';
  const navigate = useNavigate();
  const { show } = useToast();
  const listPath = `/services/waap/${serviceId}/api-protection/vulnerability`;

  const { data, isLoading } = useWaapVulnRule(domainId, ruleId);
  const appOpts = useWaapDataLossApiAppOptions(domainId);
  const assetOpts = useWaapVulnAssetOptions(domainId);

  const [rule, setRule] = useState<WaapVulnRule | null>(null);
  useEffect(() => {
    if (data) setRule(clone(data));
  }, [data]);

  const [tab, setTab] = useState<string>('detail');
  const [wl, setWl] = useState<{ key: string; operation: string; value: string }>({ key: 'app', operation: 'equal', value: '' });
  const [wlEdit, setWlEdit] = useState<number | null>(null);

  const update = useUpdateWaapVulnRule(domainId ?? '', {
    onSuccess: () => {
      // track feature engagement
      trackFeatureAction('waap', 'api_protection_vulnerability_rule', 'edit');
      show({ tone: 'success', title: 'Rule saved' });
      navigate(listPath);
    },
    onError: (e) => show({ tone: 'danger', title: 'Save failed', description: errMessage(e) }),
  });

  const crumbs = (
    <WaapConsoleBreadcrumbs
      trail={[
        { label: 'API Protection' },
        { label: 'Vulnerability Detection', slug: 'api-protection/vulnerability' },
        { label: 'Managed Rule' },
      ]}
    />
  );

  if (isLoading || !rule)
    return (
      <Container size="xl">
        <PageHeader title="Managed Rule" />
        <Skeleton height={320} />
      </Container>
    );

  const cfg = rule.config;
  const patchConfig = (patch: Partial<WaapVulnRule['config']>) =>
    setRule((r) => (r ? { ...r, config: { ...r.config, ...patch } } : r));
  // Value options depend on the metric: Application → app options, API → asset options.
  const valueOpts = (key: string) => (key === 'api' ? assetOpts.data : appOpts.data) ?? [];
  const optLabel = (v: string, key: string) => valueOpts(key).find((o) => o.value === v)?.label ?? v;

  const submitWhitelist = () => {
    if (!wl.value) return;
    const entry = { key: wl.key, operation: wl.operation, value: [wl.value] };
    patchConfig({
      whitelist: wlEdit === null ? [...cfg.whitelist, entry] : cfg.whitelist.map((w, i) => (i === wlEdit ? entry : w)),
    });
    setWl({ key: 'app', operation: 'equal', value: '' });
    setWlEdit(null);
  };
  const startEditWhitelist = (i: number) => {
    const w = cfg.whitelist[i];
    setWl({ key: w.key, operation: w.operation, value: w.value[0] ?? '' });
    setWlEdit(i);
  };
  const removeWhitelist = (i: number) => {
    patchConfig({ whitelist: cfg.whitelist.filter((_, idx) => idx !== i) });
    if (wlEdit === i) {
      setWl({ key: 'app', operation: 'equal', value: '' });
      setWlEdit(null);
    }
  };

  const save = () => update.mutate({ id: rule.id, body: rule });

  return (
    <Container size="xl">
      <PageHeader breadcrumbs={crumbs} title={rule.name} />

      <Tabs value={tab} onValueChange={setTab} style={{ marginBottom: 'var(--vn-space-4)' }}>
        <TabsList>
          {TABS.map((t) => (
            <TabsTrigger key={t.slug} value={t.slug}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div style={{ display: 'grid', gap: 'var(--vn-space-5)' }}>
        {tab === 'detail' ? (
          <Card flat>
            <CardHeader>
              <CardTitle>Detail</CardTitle>
              <CardDescription>Managed rule metadata (read-only).</CardDescription>
            </CardHeader>
            <CardBody>
              <div className={styles.form}>
                <Field label="Name">{(p) => <Input {...p} value={rule.name} disabled />}</Field>
                <Field label="Category">{(p) => <Input {...p} value={cfg.type} disabled />}</Field>
                <Field label="Description">{(p) => <Textarea {...p} value={cfg.description} disabled />}</Field>
                <Field label="Suggestion">{(p) => <Textarea {...p} value={cfg.suggestion} disabled />}</Field>
                <Field label="Risk Level">{(p) => <Input {...p} value={cfg.level} disabled />}</Field>
              </div>
            </CardBody>
          </Card>
        ) : (
          <Card flat>
            <CardHeader>
              <CardTitle>Whitelist</CardTitle>
              <CardDescription>Configure the metric, matching condition and value of the APIs to add to the whitelist.</CardDescription>
            </CardHeader>
            <CardBody>
              <div className={styles.form}>
                <div className={styles.condRow}>
                  <Select value={wl.key} onValueChange={(v) => setWl((c) => ({ ...c, key: v, value: '' }))}>
                    <SelectTrigger className={styles.condSelect}><SelectValue /></SelectTrigger>
                    <SelectContent>{WL_METRICS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={wl.operation} onValueChange={(v) => setWl((c) => ({ ...c, operation: v }))}>
                    <SelectTrigger className={styles.condSelect}><SelectValue /></SelectTrigger>
                    <SelectContent>{WL_MATCHES.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={wl.value} onValueChange={(v) => setWl((c) => ({ ...c, value: v }))}>
                    <SelectTrigger className={styles.condValue}><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{valueOpts(wl.key).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button variant="secondary" size="sm" onClick={submitWhitelist} disabled={!wl.value}>
                    {wlEdit === null ? 'Add' : 'Update'}
                  </Button>
                </div>

                {cfg.whitelist.length > 0 && (
                  <div className={styles.condList}>
                    <div className={styles.condHead}>
                      <span>Metrics</span>
                      <span>Matching Condition</span>
                      <span>Value</span>
                      <span>Operation</span>
                    </div>
                    {cfg.whitelist.map((w, i) => (
                      <div key={i} className={styles.condItem}>
                        <span>{WL_METRICS.find((m) => m.value === w.key)?.label ?? w.key}</span>
                        <span className={styles.muted}>{WL_MATCHES.find((m) => m.value === w.operation)?.label ?? w.operation}</span>
                        <span>{w.value.map((v) => optLabel(v, w.key)).join(', ')}</span>
                        <div className={styles.actions}>
                          <Button variant="ghost" size="sm" onClick={() => startEditWhitelist(i)}>Edit</Button>
                          <Button variant="ghost" size="sm" onClick={() => removeWhitelist(i)}>Delete</Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardBody>
          </Card>
        )}

        <div className={styles.formActions}>
          <Button variant="secondary" onClick={() => navigate(listPath)}>Cancel</Button>
          <Button disabled={update.isPending} onClick={save}>{update.isPending ? 'Saving…' : 'Save'}</Button>
        </div>
      </div>
    </Container>
  );
}
